import { formatDollars, truncateAddress } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import ArrowUpRightIcon from 'assets/svg/app/arrow-up-right-tg.svg'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { type FC, type MouseEvent, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { WatcherModeIcon } from 'sections/futures/WatcherMode/WatchOrConnectWallet'
import styled from 'styled-components'
import { useEnsName } from 'wagmi'

import CloseIcon from 'assets/svg/app/close.svg'

import EyeIcon from 'assets/svg/app/eye.svg'
import SignOutIcon from 'assets/svg/app/sign-out.svg'
import CopyCheckIcon from 'assets/svg/referrals/copy-check.svg'
import Button from 'components/Button'
import PointsRankAvatar from 'components/PointsRankAvatar'
import Spacer from 'components/Spacer'
import { Body, Heading } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { EXTERNAL_LINKS } from 'constants/links'
import ROUTES from 'constants/routes'
import Connector from 'containers/Connector'
import OneClickTradingSetupMessage from 'sections/futures/Trade/OneClickTradingSetupMessage'
import { REFFERAL_TIERS } from 'sections/referrals/constants'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { selectIsolatedActivePositionsMargin } from 'state/futures/isolatedMargin/selectors'
import { selectAvailableMargin, selectTotalUnrealizedPnl } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector, useFetchAction } from 'state/hooks'
import { selectPointsLevel } from 'state/points/selectors'
import { fetchReferralNftForAccount } from 'state/referrals/action'
import { selectReferralNft } from 'state/referrals/selectors'
import { selectIsWatcherMode, selectWallet } from 'state/wallet/selectors'
import { resetButtonCSS } from 'styles/common'

import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { DrawerTab } from 'sections/accounts/constants'

import { providerIsCrossMargin } from 'utils/futures'
import SubAccountsManagement from './SubAccountsManagement'
import VipAndReferralStatus from './VipAndReferralStatus'

type AccountPanelProps = {
	onDismiss: () => void
}

const AccountPanel: FC<AccountPanelProps> = memo(({ onDismiss }) => {
	const dispatch = useAppDispatch()
	const { t } = useTranslation()
	const router = useRouter()
	const availableMargin = useAppSelector(selectAvailableMargin)

	const perpsProvider = useAppSelector(selectPerpsProvider)
	const remainingMargin = useAppSelector(selectIsolatedActivePositionsMargin)
	const upnl = useAppSelector(selectTotalUnrealizedPnl)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const walletAddress = useAppSelector(selectWallet)
	const truncatedWalletAddress = truncateAddress(walletAddress ?? '')
	const level = useAppSelector(selectPointsLevel)

	const { data: ensName } = useEnsName({ address: walletAddress, chainId: 1 })
	const tier = useAppSelector(selectReferralNft)
	const { backgroundImage } = REFFERAL_TIERS[tier === -1 ? 0 : tier]
	const { copyToClipboard, CopyTooltip } = useCopyToClipboard()

	const { isAaActive, logout } = Connector.useContainer()

	const handleDisconnect = useCallback(() => {
		if (isWatcherMode) {
			onDismiss()
			return
		}

		logout()
		onDismiss()
	}, [isWatcherMode, onDismiss, logout])

	const allocatedMargin = useMemo(() => {
		return wei(remainingMargin).sub(upnl)
	}, [remainingMargin, upnl])

	const balance = useMemo(() => {
		if (providerIsCrossMargin(perpsProvider)) {
			return availableMargin
		}

		return allocatedMargin.add(wei(availableMargin)).add(wei(upnl))
	}, [perpsProvider, allocatedMargin, availableMargin, upnl])

	const walletLabel = useMemo(() => {
		return ensName || truncatedWalletAddress!
	}, [ensName, truncatedWalletAddress])

	useFetchAction(fetchReferralNftForAccount)

	const pages = useMemo(
		() => [
			{
				label: t('account.tabs.account'),
				onClick: () => {
					router.push(ROUTES.Account.Home)
					onDismiss()
				},
			},
			{
				label: t('account.tabs.delegates'),
				onClick: () => {
					router.push(ROUTES.Account.Delegates)
					onDismiss()
				},
			},
			{
				label: t('account.tabs.referrals'),
				onClick: () => {
					router.push(ROUTES.Account.Referrals)
					onDismiss()
				},
			},
			{
				label: t('account.tabs.support'),
				onClick: () => window.open(EXTERNAL_LINKS.Social.Discord, '_blank'),
				icon: <ArrowUpRightIcon width={12} height={12} />,
			},
		],
		[t, router, onDismiss]
	)

	const buttons = useMemo(
		() => [
			{
				label: t('account.account-section.deposit'),
				onClick: () => {
					dispatch(
						setOpenModalWithParams({
							type: 'manage_wallet',
							params: { tab: DrawerTab.DEPOSIT },
						})
					)
				},
			},
			{
				label: t('account.account-section.withdraw'),
				onClick: () => {
					dispatch(
						setOpenModalWithParams({
							type: 'manage_wallet',
							params: { tab: DrawerTab.WITHDRAW },
						})
					)
				},
			},
		],
		[dispatch, t]
	)

	const handleOpenEnableWatcherMode = useCallback(() => {
		dispatch(setOpenModal('enable_watcher_mode'))
	}, [dispatch])

	return (
		<Container>
			<ProifleBackground>
				<DismissButton onClick={onDismiss}>
					<CloseIcon width={18} height={18} />
				</DismissButton>
				<BackgroundImageContainer
					src={backgroundImage}
					alt="account-management-background"
					width={398}
					height={150}
				/>
			</ProifleBackground>
			<ProfileIconContainer>
				<PointsRankAvatar width={65} height={65} level={level} className="avatar-with-shadow" />
			</ProfileIconContainer>
			<ProfileContainer>
				<FlexDivRow>
					<FlexDivCol rowGap="4px">
						<StyledHeading variant="h5">{walletLabel}</StyledHeading>
						<FlexDivRowCentered justifyContent="flex-start" columnGap="3px">
							<WalletLabel color="secondary">{truncatedWalletAddress}</WalletLabel>
							{walletAddress && (
								<StyledCopyCheckIcon
									onClick={(event: MouseEvent) => copyToClipboard(event, walletAddress)}
								/>
							)}
							{isWatcherMode && (
								<WatcherContainer columnGap="5px">
									<IconContainer>
										<EyeIcon />
									</IconContainer>
									<Body color="preview" weight="semiBold">
										{t('account.account-section.watch')}
									</Body>
								</WatcherContainer>
							)}
						</FlexDivRowCentered>
						<CopyTooltip />
					</FlexDivCol>
					<VipAndReferralStatus onDismiss={onDismiss} />
				</FlexDivRow>
				<Divider height={1} divider />
				<FlexDivRowCentered columnGap="8px">
					<FlexDivCol rowGap="4px">
						<Body color="secondary">{t('account.account-section.connected-wallet')}</Body>
						<Body color="primary" weight="semiBold" size="large" mono>
							{formatDollars(balance)}
						</Body>
					</FlexDivCol>
					{isAaActive && (
						<FlexDivRow columnGap="8px">
							{buttons.map(({ label, onClick }) => (
								<Button size="small" key={label} onClick={onClick}>
									{label}
								</Button>
							))}
						</FlexDivRow>
					)}
				</FlexDivRowCentered>
				<OneClickTradingSetupMessage />
				<Divider height={1} divider />
				<PagesContainer>
					{pages.map(({ label, onClick, icon }) => (
						<RowContainer key={label} onClick={onClick}>
							<Body color="primary" size="large" weight="bold">
								{label}
							</Body>
							{icon && icon}
						</RowContainer>
					))}
				</PagesContainer>
				<Divider height={1} divider />
				<SubAccountsManagement />
			</ProfileContainer>
			<Footer columnGap="10px">
				<StyledButton size="small" fullWidth onClick={handleOpenEnableWatcherMode}>
					<WatcherModeIcon /> {t('common.wallet.watch')}
				</StyledButton>
				<StyledButton size="small" fullWidth onClick={handleDisconnect}>
					<SignOutIcon /> {t('account.tabs.signout')}
				</StyledButton>
			</Footer>
		</Container>
	)
})

const BackgroundImageContainer = styled(Image)`
	border-radius: 4px;
	object-fit: cover;
`

const PagesContainer = styled(FlexDivCol)`
	margin: -10px -15px;
`

const Container = styled(FlexDivCol)`
	position: relative;
	height: 100%;
	min-height: 400px;
	overflow-y: scroll;
`

const StyledButton = styled(Button)`
	column-gap: 5px;
`

const Footer = styled(FlexDivRow)`
	position: fixed;
	bottom: 0;
	width: 100%;
	border-top: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	padding: 15px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
`

const RowContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	padding: 7.5px 15px;
	&:hover {
		opacity: 0.8;
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
	}
`

const StyledCopyCheckIcon = styled(CopyCheckIcon)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
	cursor: pointer;
`

const WalletLabel = styled(Body)`
	padding-right: 10px;
`

const WatcherContainer = styled(FlexDivRowCentered)`
	padding-left: 10px;
	border-left: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
`

const IconContainer = styled.div`
	svg {
		path {
			fill: #ffb800;
		}
	}
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: absolute;
	top: 15px;
	right: 15px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const ProfileIconContainer = styled.div`
	position: absolute;
	top: 110px;
	padding-left: 15px;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const ProfileContainer = styled(FlexDivCol)`
	position: relative;
	padding: 0 15px;
	row-gap: 25px;
	margin-bottom: 60px;
`

const StyledHeading = styled(Heading)`
	margin-top: 40px;
	font-weight: 700;
`

const ProifleBackground = styled.div`
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	border-bottom: ${(props) => props.theme.colors.selectedTheme.newTheme.border.style};
	padding: 0;
	height: 150px;
	border-radius: 4px 4px 0 0;
`

export default AccountPanel
